import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Trash2, User } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Switch } from "./ui/switch";

export const PhotoUpload = ({ resumeId, photoPath, showPhoto, onToggle, onChange }) => {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  useEffect(() => {
    if (!photoPath) { setSrc(null); return; }
    let url;
    api.get(`/resumes/${resumeId}/photo`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(null));
    return () => url && URL.revokeObjectURL(url);
  }, [resumeId, photoPath]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be under 5 MB");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/resumes/${resumeId}/photo`, form);
      onChange(data.photo_path);
      toast.success("Photo added");
    } catch (e) { toast.error(errMsg(e, "Upload failed")); }
    setBusy(false);
    input.current.value = "";
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/resumes/${resumeId}/photo`); onChange(null); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-5 mb-6" data-testid="photo-upload">
      <div className="relative h-20 w-20 rounded-md overflow-hidden bg-[#0c0e14] border border-white/10 flex items-center justify-center shrink-0">
        {src ? <img src={src} alt="Your photo" className="h-full w-full object-cover" data-testid="photo-upload-preview" /> : <User className="h-8 w-8 text-slate-600" />}
        {busy && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-400" /></div>}
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium">Profile photo <span className="text-slate-500 font-normal">(optional)</span></p>
        <div className="flex items-center gap-2">
          <button onClick={() => input.current.click()} disabled={busy} data-testid="photo-upload-btn"
            className="rounded-full bg-white/10 hover:bg-white/20 text-sm px-4 py-1.5 flex items-center gap-1.5 transition-colors disabled:opacity-50">
            <Camera className="h-3.5 w-3.5" /> {photoPath ? "Change" : "Upload photo"}
          </button>
          {photoPath && (
            <button onClick={remove} disabled={busy} data-testid="photo-remove-btn" className="p-2 text-slate-500 hover:text-red-400 transition-colors" aria-label="Remove photo">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500">JPG, PNG or WEBP · up to 5 MB · cropped to a square</p>
        {photoPath && (
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" htmlFor="show-photo-switch">
            <Switch id="show-photo-switch" checked={showPhoto} onCheckedChange={onToggle} data-testid="photo-visibility-switch" className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/25" />
            {showPhoto ? "Shown on resume" : "Hidden from resume"}
          </label>
        )}
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="photo-file-input" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
};
